export interface GiftCardProviderEntity {
  /**
   * Dummy id for docs generation.
   * @format GUID
   */
  _id?: string | null;
}

export interface GetBalanceRequest {
  /**
   * Gift card code.
   * @minLength 8
   * @maxLength 20
   */
  code?: string;
  /**
   * App ID of the Gift Card provider. Deprecated.
   * @format GUID
   * @deprecated App ID of the Gift Card provider. Deprecated.
   * @targetRemovalDate 2025-07-01
   */
  appInstanceId?: string;
  /**
   * The physical location ID. Can be based on the Locations API or an external provider.
   * @maxLength 50
   */
  locationId?: string | null;
  /**
   * Gift card PIN.
   * @maxLength 50
   */
  pin?: string | null;
  /** [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields). */
  extendedFields?: ExtendedFields;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GetBalanceResponse {
  /**
   * Current balance.
   * @max 999999999.99
   */
  balance?: number;
  /**
   * Currency code.
   * @format CURRENCY
   */
  currencyCode?: string;
  /**
   * External ID in the gift card provider's system.
   * Used for integration and tracking across different platforms.
   * @minLength 1
   * @maxLength 50
   */
  externalId?: string | null;
}

export interface RedeemRequest {
  /**
   * Gift card code.
   * @minLength 8
   * @maxLength 20
   */
  code?: string;
  /**
   * App ID of the Gift Card provider. Deprecated.
   * @format GUID
   * @deprecated App ID of the Gift Card provider. Deprecated.
   * @targetRemovalDate 2025-07-01
   */
  appInstanceId?: string;
  /**
   * Amount to redeem from the gift card.
   * @max 999999999.99
   */
  amount?: number;
  /**
   * Order ID the gift card transaction is applied to. Order details can be collected from eCommerce Search Orders.
   * @format GUID
   */
  orderId?: string;
  /**
   * Currency code.
   * @format CURRENCY
   */
  currencyCode?: string;
  /**
   * Physical location ID. Can be based on the Locations API or an external provider.
   * @maxLength 50
   */
  locationId?: string | null;
  /**
   * Gift card PIN.
   * @maxLength 50
   */
  pin?: string | null;
  /**
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   * @internal
   */
  extendedFields?: ExtendedFields;
}

export interface RedeemResponse {
  /**
   * Remaining balance on the Gift Card after the redemption.
   * @max 999999999.99
   */
  remainingBalance?: number;
  /**
   * Currency code.
   * @format CURRENCY
   */
  currencyCode?: string;
  /**
   * Transaction ID.
   * @minLength 1
   * @maxLength 100
   */
  transactionId?: string;
}

export interface VoidRequest {
  /**
   * App ID of the Gift Card provider. Deprecated.
   * @format GUID
   * @deprecated App ID of the Gift Card provider. Deprecated.
   * @targetRemovalDate 2025-07-01
   */
  appInstanceId?: string;
  /**
   * Transaction ID to void.
   * @minLength 1
   * @maxLength 100
   */
  transactionId?: string;
  /**
   * Physical location ID. Can be based on the Locations API or an external provider.
   * @maxLength 50
   */
  locationId?: string | null;
  /**
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   * @internal
   */
  extendedFields?: ExtendedFields;
}

export interface VoidResponse {
  /**
   * Remaining balance on the Gift Card after voiding the transaction.
   * @max 999999999.99
   */
  remainingBalance?: number;
  /**
   * Currency code.
   * @format CURRENCY
   */
  currencyCode?: string;
}

export interface GiftCardProviderConfig {
  /** Base URI where the endpoints are called. Wix eCommerce appends the endpoint path to the base URI. For example, to call the Get Balance endpoint at `https://my-gift-cards.com/v1/balance`, the base URI you provide here is `https://my-gift-cards.com/`. */
  deploymentUri?: string;
}

/**
 * this message is not directly used by any service,
 * it exists to describe the expected parameters that SHOULD be provided to invoked Velo methods as part of open-platform.
 * e.g. SPIs, event-handlers, etc..
 * NOTE: this context object MUST be provided as the last argument in each Velo method signature.
 *
 * Example:
 * ```typescript
 * export function wixStores_onOrderCanceled({ event, metadata }: OrderCanceledEvent) {
 * ...
 * }
 * ```
 */
export interface Context {
  /** A unique identifier of the request. You may print this ID to your logs to help with future debugging and easier correlation with Wix's logs. */
  requestId?: string | null;
  /**
   * [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) 3-letter currency code.
   * @format CURRENCY
   */
  currency?: string | null;
  /** An object that describes the identity that triggered this request. */
  identity?: IdentificationData;
  /** A string representing a language and region in the format of `"xx-XX"`. First 2 letters represent the language code according to ISO 639-1. This is followed by a dash "-", and then a by 2 capital letters representing the region according to ISO 3166-2. For example, `"en-US"`. */
  languages?: string[];
  /**
   * The service provider app's instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Extension ID in Dev Center.
   * @internal
   * @maxLength 256
   */
  appExtensionId?: string | null;
  /**
   * Extension type in Dev Center.
   * @internal
   * @maxLength 256
   */
  appExtensionType?: string | null;
  /**
   * Invoked function.
   * @internal
   * @maxLength 256
   */
  functionName?: string | null;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: IdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}
